// components
import { Button, IconButton, Menu, Tooltip } from '@mui/material';
import {
  DateCalendar,
  DateRangeCalendar,
  DateView
} from '@mui/x-date-pickers-pro';
import { WeekPickerDay } from './components/WeekPickerDay';

// hooks
import { useDispatch, useSelector } from 'hooks';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { setUserPreference } from 'redux/actionCreators';
import { useDateNavigationStore } from './DateNavigation.stores';

// styles
import {
  Container,
  PeriodContainer,
  PeriodTitle,
  PeriodSubtitle,
  DateNavigationButton,
  DateNavigationMenuContainer,
  PeriodChip,
  TodayButtonContainer,
  StyledCaretIconBox
} from './DateNavigation.styles';

// types
import {
  DateNavigationProps,
  Period,
  SingleOrRange
} from './DateNavigation.types';
import { WeekPickerDayProps } from './components/WeekPickerDay.types';

//  utils
import { addWeeks, isToday } from 'date-fns';
import { capitalize } from 'lodash';
import { formatByLocale } from 'utils/date';
import {
  getFormatByPeriod,
  nextDate,
  previousDate
} from './DateNavigation.utils';
import useSelectedDate from 'components/roster-portal/activity-planning/hooks/useSelectedDate';
import { Icon } from '@sdb/planning-ui';
import { IconContainer } from 'components/mrp/mrp.styles';
import { StyledBoxFlexCenterGap } from '../Box/Box.styles';
import dayjs from 'dayjs';

export default function DateNavigation({
  allowMenuOptions,
  defaultDate: _defaultDate,
  defaultDateRange,
  defaultPeriod,
  disabled,
  onDateChange,
  onPeriodChange,
  options = [],
  isToolbar = false,
  isRightPositioned = false
}: DateNavigationProps) {
  const dispatch = useDispatch();
  const { t } = useTranslation('common');

  const [previouslySelectedDate, setSelectedDate] = useSelectedDate();
  const { period, setPeriod } = useDateNavigationStore((state) => state);
  const [dateRangeAnchor, setDateRangeAnchor] =
    useState<HTMLButtonElement | null>(null);
  const datePickerOpen = Boolean(dateRangeAnchor);

  const holidays = useSelector((state) => state.holidays);
  const defaultDateNavigationPeriod = useSelector(
    (state) => state.preferences.defaultDateNavigationPeriod
  );

  const defaultDate = _defaultDate || previouslySelectedDate;
  const isDisabled = disabled || !period;

  let showIsHoliday = false;
  let showIsToday = false;
  let dayDescription = '';

  if (period == 'day') {
    const holiday = holidays[formatByLocale(defaultDate, 'yyyy-MM-dd')];
    showIsHoliday = Boolean(holiday);
    showIsToday = isToday(defaultDate);
    dayDescription = showIsToday ? t('today') : holiday?.description || '';
  }

  const handleDateChange = (date: SingleOrRange | null) => {
    if (!Array.isArray(date) && date && period !== 'custom') {
      setSelectedDate(date as Date);
    }

    if (
      (period === 'fourWeeks' || period === 'eightWeeks') &&
      date &&
      !Array.isArray(date)
    ) {
      onDateChange?.(dayjs(date).startOf('week').toDate());
    } else {
      onDateChange?.(date);
    }
  };

  const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
    setDateRangeAnchor(event.currentTarget);
  };

  const handleClose = () => {
    setDateRangeAnchor(null);
  };

  const savePreference = (period: Period) => {
    dispatch(
      setUserPreference({
        key: 'defaultDateNavigationPeriod',
        value: period
      })
    );
  };

  const dateCalendarView = (period: Period | null): readonly DateView[] => {
    switch (period) {
      case 'year':
        return ['year'];
      case 'month':
        return ['month', 'year'];
      case 'fourWeeks':
      case 'eightWeeks':
        return ['day', 'month', 'year'];
      default:
        return ['day'];
    }
  };

  const dateForLabel =
    period === 'custom'
      ? defaultDateRange ?? undefined
      : defaultDate ?? undefined;
  const periodLabel = dateForLabel
    ? capitalize(getFormatByPeriod(dateForLabel, period))
    : '';

  const periodDescription = (showWeek = false) => {
    let startWeek: number;
    let endWeek: number;

    switch (period) {
      case 'day':
        startWeek = dayjs(defaultDate).week();
        return (
          `${capitalize(formatByLocale(defaultDate, 'EEEE'))}` +
          (showWeek ? ` (${capitalize(t('week'))} ${startWeek})` : '')
        );
      case 'week':
      case 'workweek':
        startWeek = dayjs(defaultDate).week();
        return capitalize(t(period)) + (showWeek ? ` ${startWeek}` : '');
      case 'fourWeeks':
        startWeek = dayjs(defaultDate).startOf('week').week();
        endWeek = dayjs(addWeeks(defaultDate, 3)).endOf('week').week(); // 3 weeks + endOfWeek
        return (
          capitalize(t(period)) +
          (showWeek
            ? ` (${capitalize(t('week'))} ${startWeek} - ${endWeek})`
            : '')
        );
      case 'eightWeeks':
        startWeek = dayjs(defaultDate).startOf('week').week();
        endWeek = dayjs(addWeeks(defaultDate, 7)).endOf('week').week(); // 7 weeks + endOfWeek
        return (
          capitalize(t(period)) +
          (showWeek
            ? ` (${capitalize(t('week'))} ${startWeek} - ${endWeek})`
            : '')
        );
      case 'month':
        startWeek = dayjs(defaultDate).startOf('month').week();
        endWeek = dayjs(defaultDate).endOf('month').week();
        return (
          capitalize(t(period)) +
          (showWeek
            ? ` (${capitalize(t('week'))} ${startWeek} - ${endWeek})`
            : '')
        );
      default:
        return capitalize(t(period ?? ''));
    }
  };

  // Set initial period if passed
  useEffect(() => {
    if (!allowMenuOptions) {
      return setPeriod(defaultPeriod);
    }

    if (defaultDateNavigationPeriod || defaultPeriod) {
      setPeriod(
        options?.includes(defaultDateNavigationPeriod)
          ? defaultDateNavigationPeriod
          : defaultPeriod
      );
    }
  }, [defaultDateNavigationPeriod, defaultPeriod]);

  useEffect(() => {
    if (period) onPeriodChange?.(period);
  }, [period]);

  return (
    <Container isToolbar={isToolbar} isRightPositioned={isRightPositioned}>
      {period === 'day' && (
        <Tooltip title={t('previousWeek') || ''} arrow>
          <IconButton
            color="inherit"
            className="prev-btn"
            size="small"
            onClick={previousDate.bind(
              null,
              defaultDate,
              'week',
              handleDateChange
            )}
            disabled={isDisabled}
          >
            <Icon type="angles-left" fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {period !== 'custom' && !isRightPositioned && (
        <Tooltip
          title={
            t(
              'previous' +
                (period === 'fourWeeks' || period === 'eightWeeks'
                  ? 'Week'
                  : capitalize(period?.toString()))
            ) || ''
          }
          arrow
        >
          <IconButton
            className="prev-btn"
            color="inherit"
            size="small"
            onClick={previousDate.bind(
              null,
              defaultDate,
              period as Period,
              handleDateChange
            )}
            disabled={isDisabled}
          >
            <Icon type="chevron-left" fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      <Tooltip title={dayDescription} arrow>
        <DateNavigationButton
          variant="text"
          showIsToday={showIsToday}
          showIsHoliday={showIsHoliday}
          startIcon={<Icon type="calendar-week" />}
          {...(allowMenuOptions &&
            !isRightPositioned && {
              endIcon: (
                <IconContainer iconSize={isToolbar ? 'SMALL' : 'MEDIUM'}>
                  <StyledCaretIconBox datePickerOpen={datePickerOpen}>
                    <Icon
                      type={isToolbar ? 'chevron-down' : 'caret-down'}
                      fontSize="small"
                    />
                  </StyledCaretIconBox>
                </IconContainer>
              )
            })}
          disabled={isDisabled}
          onClick={handleClick}
          isToolbar={isToolbar}
          isRightPositioned={isRightPositioned}
        >
          {!isRightPositioned && (
            <PeriodContainer isToolbar={isToolbar}>
              {isToolbar ? (
                <PeriodTitle>{periodDescription(false)}</PeriodTitle>
              ) : (
                <PeriodSubtitle>{periodDescription(true)}</PeriodSubtitle>
              )}

              <PeriodTitle>{periodLabel}</PeriodTitle>
            </PeriodContainer>
          )}
        </DateNavigationButton>
      </Tooltip>

      <Menu
        anchorEl={dateRangeAnchor}
        open={datePickerOpen}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'center'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'center'
        }}
      >
        {allowMenuOptions && (
          <DateNavigationMenuContainer>
            {options.map((periodOption: Period, i: number) => (
              <PeriodChip
                variant={period == periodOption ? 'filled' : 'outlined'}
                key={i}
                onClick={() => {
                  setPeriod(periodOption);
                  savePreference(periodOption);
                }}
                label={t(`timeUnits.${periodOption.toUpperCase()}`)}
              />
            ))}
          </DateNavigationMenuContainer>
        )}

        {period === 'custom' ? (
          <DateRangeCalendar
            calendars={2}
            value={defaultDateRange ?? undefined}
            displayWeekNumber
            onChange={(date) => {
              handleDateChange(date);
            }}
          />
        ) : (
          <DateCalendar
            onChange={(date) => {
              handleDateChange(date);
              setDateRangeAnchor(null);
            }}
            defaultValue={defaultDate}
            disabled={isDisabled}
            displayWeekNumber
            views={dateCalendarView(period)}
            slots={{
              day:
                period === 'week' ||
                period === 'workweek' ||
                period === 'fourWeeks' ||
                period === 'eightWeeks'
                  ? WeekPickerDay
                  : undefined
            }}
            slotProps={{
              day: {
                anchor: defaultDate,
                workingWeek: period === 'workweek',
                fourWeeks: period === 'fourWeeks',
                eightWeeks: period === 'eightWeeks',
                weekStartsOn: 1 // monday
              } as WeekPickerDayProps
            }}
          />
        )}
        <StyledBoxFlexCenterGap>
          <TodayButtonContainer>
            <Button
              variant="text"
              onClick={() => {
                if (period === 'custom') {
                  handleDateChange([new Date(), null]);
                } else {
                  handleDateChange(new Date());
                  setDateRangeAnchor(null);
                }
              }}
            >
              {t('today')}
            </Button>
          </TodayButtonContainer>
        </StyledBoxFlexCenterGap>
      </Menu>

      {period !== 'custom' && !isRightPositioned && (
        <Tooltip
          title={
            t(
              'next' +
                (period === 'fourWeeks' || period === 'eightWeeks'
                  ? 'Week'
                  : capitalize(period?.toString()))
            ) || ''
          }
          arrow
          enterDelay={300}
          enterNextDelay={300}
        >
          <IconButton
            color="inherit"
            className="next-btn"
            size="small"
            onClick={nextDate.bind(
              null,
              defaultDate,
              period as Period,
              handleDateChange
            )}
            disabled={isDisabled}
          >
            <Icon type="chevron-right" fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
      {period === 'day' && (
        <Tooltip title={t('nextWeek') || ''} arrow>
          <IconButton
            color="inherit"
            className="next-btn"
            size="small"
            onClick={nextDate.bind(null, defaultDate, 'week', handleDateChange)}
            disabled={isDisabled}
          >
            <Icon type="angles-right" fontSize="small" />
          </IconButton>
        </Tooltip>
      )}
    </Container>
  );
}
